import { useState } from 'react'
import { api } from '../lib/api'

export interface UserRow {
  id: string
  email: string
  full_name: string
  role: string
  is_active: boolean
  must_change_password: boolean
  employee_number: string | null
  department_id: string | null
  business_unit_id: string | null
  position: string | null
  last_login_at: string | null
}

interface Props {
  users: UserRow[]
  onChanged: () => void
}

export function UserTable({ users, onChanged }: Props) {
  const [busyId, setBusyId] = useState<string | null>(null)
  const [resetResult, setResetResult] = useState<{ id: string; pwd: string } | null>(null)

  async function toggleActive(u: UserRow) {
    setBusyId(u.id)
    try {
      await api(`/api/admin/users/${u.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ is_active: !u.is_active }),
      })
      onChanged()
    } finally {
      setBusyId(null)
    }
  }

  async function resetPassword(u: UserRow) {
    setBusyId(u.id)
    try {
      const res = await api<{ new_password: string }>(
        `/api/admin/users/${u.id}/reset-password`,
        { method: 'POST' },
      )
      setResetResult({ id: u.id, pwd: res.new_password })
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div className="bg-white dark:bg-zinc-800 rounded-2xl border border-zinc-200 dark:border-zinc-700 overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-zinc-50 dark:bg-zinc-900 text-zinc-600 dark:text-zinc-400 text-left">
          <tr>
            <th className="px-4 py-2 font-medium">ТН</th>
            <th className="px-4 py-2 font-medium">ФИО</th>
            <th className="px-4 py-2 font-medium">Email</th>
            <th className="px-4 py-2 font-medium">Роль</th>
            <th className="px-4 py-2 font-medium">Статус</th>
            <th className="px-4 py-2 font-medium">Действия</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700">
          {users.length === 0 && (
            <tr>
              <td colSpan={6} className="px-4 py-6 text-center text-zinc-500">
                Нет пользователей
              </td>
            </tr>
          )}
          {users.map((u) => (
            <tr key={u.id} className="text-zinc-900 dark:text-zinc-100">
              <td className="px-4 py-2">{u.employee_number ?? '—'}</td>
              <td className="px-4 py-2">{u.full_name}</td>
              <td className="px-4 py-2">{u.email}</td>
              <td className="px-4 py-2">
                <span className="px-2 py-0.5 text-xs rounded-md bg-zinc-100 dark:bg-zinc-700">
                  {u.role}
                </span>
              </td>
              <td className="px-4 py-2">
                {u.is_active ? (
                  <span className="text-green-600">активен</span>
                ) : (
                  <span className="text-zinc-500">деактивирован</span>
                )}
              </td>
              <td className="px-4 py-2 space-x-2">
                <button
                  onClick={() => void toggleActive(u)}
                  disabled={busyId === u.id}
                  className="text-xs text-blue-600 hover:underline disabled:opacity-50"
                >
                  {u.is_active ? 'Деактивировать' : 'Активировать'}
                </button>
                <button
                  onClick={() => void resetPassword(u)}
                  disabled={busyId === u.id}
                  className="text-xs text-blue-600 hover:underline disabled:opacity-50"
                >
                  Сбросить пароль
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {resetResult && (
        <div className="border-t border-zinc-200 dark:border-zinc-700 bg-amber-50 dark:bg-amber-900/30 p-4">
          <p className="text-sm font-semibold text-amber-900 dark:text-amber-100">
            Новый пароль (передайте пользователю один раз):
          </p>
          <code className="block mt-1 text-sm text-amber-900 dark:text-amber-100 font-mono">
            {resetResult.pwd}
          </code>
          <button
            onClick={() => setResetResult(null)}
            className="mt-2 text-xs text-zinc-600 dark:text-zinc-400 hover:underline"
          >
            Закрыть
          </button>
        </div>
      )}
    </div>
  )
}
